'use client'

import * as React from 'react'
import { ChevronDownIcon, MenuIcon, SearchIcon } from '@/src/icons'
import { cn } from '@/registry/reapit/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/registry/reapit/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/registry/reapit/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/registry/reapit/ui/tooltip'

// ---------------------------------------------------------------------------
// TopBar — slot-based root component matching v5 API
// ---------------------------------------------------------------------------

export namespace TopBar {
  export interface Props extends Omit<React.ComponentProps<'header'>, 'children'> {
    /** Typically an AppSwitcher component. Hidden below SM (768px). */
    appSwitcher?: React.ReactNode
    /** The user's profile menu. Typically a TopBar.AvatarMenu. Hidden below MD (1024px). */
    avatar?: React.ReactNode
    /** The product's logo. Always visible. */
    logo: React.ReactNode
    /** Main navigation region. Hidden below LG (1440px). */
    mainNav?: React.ReactNode
    /** Overflow menu for collapsed navigation items. Hidden at LG+ (1440px). */
    menu?: React.ReactNode
    /** Global search entry point. Always visible (icon-only at XS, button at SM+). */
    search?: React.ReactNode
    /** Secondary navigation (icon items). Hidden below LG (1440px). */
    secondaryNav?: React.ReactNode
  }
}

/**
 * A responsive header that contains the product's app switcher, logo, main navigation,
 * search entry point, secondary navigation, and user avatar. Uses CSS container queries
 * (not media queries) for responsive collapse behaviour.
 *
 * Only the logo is required; all other regions are optional.
 */
function TopBarRoot({
  appSwitcher,
  avatar,
  logo,
  mainNav,
  menu,
  search,
  secondaryNav,
  className,
  ...rest
}: TopBar.Props) {
  return (
    <header
      data-slot="top-bar"
      className={cn('h-14 w-full @container/topbar border-b border-border bg-background', className)}
      {...rest}
    >
      <div
        className={cn('grid h-full items-center py-2 pl-4 pr-2', '@3xl/topbar:px-4', '@min-[1440px]/topbar:px-5')}
        style={{
          gridTemplateAreas: "'app-switcher logo main-nav search secondary-nav mobile-nav profile'",
          gridTemplateColumns: 'min-content min-content 1fr auto auto auto auto',
        }}
      >
        {/* Source order matches visual order for a11y */}
        {appSwitcher && (
          <div
            className={cn('hidden pr-4', '@3xl/topbar:block', '@min-[1440px]/topbar:pr-5')}
            style={{ gridArea: 'app-switcher' }}
          >
            {appSwitcher}
          </div>
        )}
        <div className="flex items-center justify-start py-2 pr-2" style={{ gridArea: 'logo' }}>
          {logo}
        </div>
        {mainNav && (
          <div
            className={cn('hidden py-1 pl-6', '@min-[1440px]/topbar:block')}
            style={{
              gridArea: 'main-nav',
              containerName: 'top-bar-main-nav-container',
              containerType: 'inline-size',
            }}
          >
            {mainNav}
          </div>
        )}
        {search && (
          <div
            className={cn('w-12 pr-2', '@3xl/topbar:w-[216px] @3xl/topbar:py-1 @3xl/topbar:pr-4')}
            style={{ gridArea: 'search' }}
          >
            {search}
          </div>
        )}
        {secondaryNav && (
          <div className={cn('hidden pr-2', '@min-[1440px]/topbar:block')} style={{ gridArea: 'secondary-nav' }}>
            {secondaryNav}
          </div>
        )}
        {menu && (
          <div
            className={cn('block', '@5xl/topbar:pr-2', '@min-[1440px]/topbar:hidden')}
            style={{ gridArea: 'mobile-nav' }}
          >
            {menu}
          </div>
        )}
        {avatar && (
          <div className={cn('hidden py-1', '@5xl/topbar:flex')} style={{ gridArea: 'profile' }}>
            {avatar}
          </div>
        )}
      </div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// TopBar.BrandLogo — renders SVG brand mark for a given app name
// ---------------------------------------------------------------------------

const supportedAppNames = [
  'Reapit',
  'Console Owner',
  'Console Pay',
  'Console Tenant',
  'Reapit Connect',
  'Reapit Projector',
  'Reapit Sales',
  'Reapit Lettings',
  'Reapit PM',
  'PM Demo',
  'PM Sales',
  'PM Inspect',
  'Reapit Forms',
  'Reapit Websites',
  'Reapit Proposals',
  'KeyWhere',
  'Auto Responder',
] as const

type SupportedAppName = (typeof supportedAppNames)[number]

export namespace TopBarBrandLogo {
  export interface Props extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
    /** The name of the app for which to display the logo. */
    appName: SupportedAppName
    /** The URL to navigate to when the logo is clicked. Defaults to '/'. */
    href?: string
  }
}

/**
 * Displays a product logo in the Reapit brand. For the base "Reapit" app name,
 * renders the full Reapit wordmark SVG (mark + text). For other app names,
 * renders the mark + app name text.
 */
function TopBarBrandLogo({ appName, href = '/', className, ...rest }: TopBarBrandLogo.Props) {
  return (
    <a
      {...rest}
      aria-label={`Go to ${appName}`}
      href={href}
      className={cn(
        'inline-flex items-center no-underline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-1',
        className,
      )}
    >
      {appName === 'Reapit' ? (
        <ReapitWordmarkSvg />
      ) : (
        <>
          <ReapitMarkSvg />
          <span className="ml-2 whitespace-nowrap text-sm font-semibold text-foreground">{appName}</span>
        </>
      )}
    </a>
  )
}

/** The Reapit triple-block mark (coloured) — used for non-"Reapit" app logos */
function ReapitMarkSvg(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <path d="M14.4062 0L19.1877 3.14371V10L23.9991 6.76645V0H14.4062Z" fill="#98C1C2" />
      <path d="M9.5918 10L14.3733 13.1437V20L19.1846 16.7665V10H9.5918Z" fill="#D1263D" />
      <path d="M0 3.2334L4.78148 6.3771V13.2334L9.59285 9.9999V3.2334H0Z" fill="#FD9E1D" />
    </svg>
  )
}

/** Full Reapit wordmark (mark + "Reapit" text) — matches v5 brand-logo/logos/reapit.svg */
function ReapitWordmarkSvg(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="96"
      height="24"
      viewBox="0 0 96 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <path d="M14.4062 2L19.1877 5.14371V12L23.9991 8.76645V2H14.4062Z" fill="#98C1C2" />
      <path d="M9.5918 12L14.3733 15.1437V22L19.1846 18.7665V12H9.5918Z" fill="#D1263D" />
      <path d="M0 5.2334L4.78148 8.3771V15.2334L9.59285 11.9999V5.2334H0Z" fill="#FD9E1D" />
      <path
        d="M35.3051 5.18536H39.8554C41.8013 5.18536 42.6695 6.5325 42.6695 7.8497C42.6695 9.1669 41.8013 10.514 39.8554 10.514H35.3051V5.18536ZM38.4484 12.6096L42.7892 17.579H45.6332L40.993 12.3402L41.4121 12.2504C44.5255 11.6217 45.0045 9.1669 45.0045 7.78983C45.0045 4.5567 42.3402 3.08981 39.8554 3.05988H33V17.579H35.335V12.5497H38.4185L38.4484 12.6096Z"
        fill="#222B33"
      />
      <path
        d="M48.6268 11.5618L48.7166 11.2325C49.1058 9.8255 50.3032 8.98728 51.9198 8.98728C53.6561 8.98728 54.7338 9.8255 54.9134 11.2625L54.9434 11.5618H48.6268ZM55.7517 8.65798C54.8835 7.70002 53.5364 7.1911 51.8599 7.1911C48.6567 7.1911 46.5013 9.31658 46.5013 12.4599C46.5013 15.7828 48.6268 17.8485 52.0695 17.8485C53.6861 17.8485 55.2727 17.2797 56.2606 16.3816L54.9434 15.0943C54.195 15.693 52.9676 15.9924 52.1294 15.9924C50.2434 15.9924 48.9262 15.0344 48.7166 13.5077L48.6867 13.2083H57.009C57.0988 11.2624 56.6797 9.67582 55.7517 8.65798Z"
        fill="#222B33"
      />
      <path d="M86.1371 7.46053H84.0416V17.579H86.1371V7.46053Z" fill="#222B33" />
      <path
        d="M91.1365 16.8606C91.7353 17.4294 92.6333 17.7287 93.6811 17.6988C94.3697 17.6688 94.9085 17.579 95.5372 17.3395L94.9684 15.6032C94.6091 15.7529 94.19 15.8427 93.8607 15.8427C92.8429 15.8427 92.2442 15.1841 92.2442 14.0765V9.25671H95.1779V7.5204H92.2741V4.5567L90.1786 4.76625V7.5204H88.2028V9.25671H90.1786V14.0765C90.1786 15.3039 90.5079 16.2319 91.1365 16.8606Z"
        fill="#222B33"
      />
      <path
        d="M63.7746 15.8727C61.9485 15.8727 60.4816 14.3758 60.4816 12.5797C60.4816 10.7835 61.9785 9.28665 63.7746 9.28665C65.5708 9.28665 67.0676 10.7835 67.0676 12.5797C67.0676 14.3758 65.6008 15.8727 63.7746 15.8727ZM69.1931 17.5491V7.49046H67.1575V8.38855L66.918 8.20894C65.9899 7.55033 64.9122 7.1911 63.7746 7.1911C60.8109 7.1911 58.416 9.58601 58.416 12.5497C58.416 15.5134 60.8109 17.9083 63.7746 17.9083C64.9122 17.9083 65.9899 17.5491 66.918 16.8905L67.1575 16.7109V17.5491H69.1931Z"
        fill="#222B33"
      />
      <path
        d="M76.8269 9.19683C78.653 9.19683 80.1199 10.6937 80.1199 12.4898C80.1199 14.286 78.6231 15.7828 76.8269 15.7828C75.0307 15.7828 73.5339 14.286 73.5339 12.4898C73.5339 10.6937 75.0008 9.19683 76.8269 9.19683ZM76.8269 17.8485C79.7906 17.8485 82.1855 15.4535 82.1855 12.4898C82.1855 9.52613 79.7906 7.13122 76.8269 7.13122C75.6893 7.13122 74.6116 7.49046 73.6836 8.14906L73.4441 8.32868V7.49046H71.4084V21.83H73.4441V16.651L73.6836 16.8306C74.6116 17.4892 75.6893 17.8485 76.8269 17.8485Z"
        fill="#222B33"
      />
      <path
        d="M85.0894 3C84.4607 3 83.8021 3.38917 83.8021 4.28727C83.8021 5.18536 84.4607 5.57453 85.0894 5.57453C85.718 5.57453 86.3766 5.18536 86.3766 4.28727C86.3766 3.38917 85.718 3 85.0894 3Z"
        fill="#222B33"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// TopBar.MainNav — navigation list wrapper
// ---------------------------------------------------------------------------

export namespace TopBarMainNav {
  export interface Props extends React.ComponentProps<'nav'> {
    /** @default 'Main navigation' */
    'aria-label'?: string
  }
}

function TopBarMainNav({
  'aria-label': ariaLabel = 'Main navigation',
  children,
  className,
  ...rest
}: TopBarMainNav.Props) {
  return (
    <nav {...rest} aria-label={ariaLabel} className={className}>
      <ul className="m-0 flex list-none flex-row flex-nowrap items-center gap-2 p-0">{children}</ul>
    </nav>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavItem — anchor-based main nav item (wrapped in <li>)
// ---------------------------------------------------------------------------

export namespace TopBarNavItem {
  export interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    /** Whether this item represents the current page. */
    'aria-current'?: 'page' | false
    /** The URL to navigate to. */
    href: string
  }
}

function TopBarNavItem({ 'aria-current': ariaCurrent, children, className, ...rest }: TopBarNavItem.Props) {
  return (
    <li className="flex items-center">
      <a
        {...rest}
        aria-current={ariaCurrent || undefined}
        className={cn(
          'flex w-min cursor-pointer items-center justify-center whitespace-nowrap rounded-sm border-none bg-transparent px-3.5 py-1.5 text-sm font-medium text-text-secondary no-underline outline-none',
          'hover:bg-fill-neutral-lightest',
          'focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-1',
          'aria-[current=page]:bg-fill-neutral-lightest aria-[current=page]:text-text-action',
          className,
        )}
      >
        <span>{children}</span>
      </a>
    </li>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavMenuItem — button with dropdown, wrapped in <li>
// ---------------------------------------------------------------------------

export namespace TopBarNavMenuItem {
  export interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /** The visible label text on the button. */
    label: string
    /** The dropdown menu content (DropdownMenu items). */
    children: React.ReactNode
  }
}

function TopBarNavMenuItem({ label, children, className, ...rest }: TopBarNavMenuItem.Props) {
  return (
    <li className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            {...rest}
            className={cn(
              'flex w-min cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-sm border-none bg-transparent px-3.5 py-1.5 text-sm font-medium text-text-secondary outline-none',
              'hover:bg-fill-neutral-lightest',
              'focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-1',
              className,
            )}
            type="button"
          >
            <span>{label}</span>
            <span className="size-4 text-icon-primary transition-transform duration-200" aria-hidden="true">
              <ChevronDownIcon />
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">{children}</DropdownMenuContent>
      </DropdownMenu>
    </li>
  )
}

// ---------------------------------------------------------------------------
// TopBar.SecondaryNav — icon navigation list wrapper
// ---------------------------------------------------------------------------

export namespace TopBarSecondaryNav {
  export interface Props extends React.ComponentProps<'nav'> {
    /** @default 'Secondary navigation' */
    'aria-label'?: string
  }
}

function TopBarSecondaryNav({
  'aria-label': ariaLabel = 'Secondary navigation',
  children,
  className,
  ...rest
}: TopBarSecondaryNav.Props) {
  return (
    <nav {...rest} aria-label={ariaLabel} className={className}>
      <ul className="m-0 flex list-none flex-row flex-nowrap items-center gap-2 p-0">{children}</ul>
    </nav>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavIconItem — icon-only anchor link (wrapped in <li>)
// ---------------------------------------------------------------------------

export namespace TopBarNavIconItem {
  export interface Props extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
    /** Whether this item represents the current page. */
    'aria-current'?: 'page' | false
    /** The accessible name. */
    'aria-label': string
    /** Optional notification badge. */
    hasBadge?: boolean
    /** The URL to navigate to. */
    href: string
    /** The nav item's icon. */
    icon: React.ReactNode
  }
}

function TopBarNavIconItem({
  'aria-current': ariaCurrent,
  'aria-label': ariaLabel,
  hasBadge,
  icon,
  className,
  ...rest
}: TopBarNavIconItem.Props) {
  return (
    <li className="flex items-center">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              {...rest}
              aria-label={ariaLabel}
              aria-current={ariaCurrent || undefined}
              className={cn(
                'relative inline-flex cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-2 text-neutral-400 outline-none',
                'hover:bg-fill-neutral-lightest',
                'focus-visible:outline-2 focus-visible:outline-ring',
                'aria-[current=page]:bg-fill-neutral-lightest aria-[current=page]:text-text-action',
                className,
              )}
            >
              <span
                className="flex size-6 items-center justify-center text-inherit [&>svg]:size-full"
                aria-hidden="true"
              >
                {icon}
              </span>
              {hasBadge && <span className="absolute right-[5px] top-[5px] size-2 rounded-full bg-destructive" />}
            </a>
          </TooltipTrigger>
          <TooltipContent side="bottom">{ariaLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </li>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavIconMenuItem — icon-only button with dropdown (wrapped in <li>)
// ---------------------------------------------------------------------------

export namespace TopBarNavIconMenuItem {
  export interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /** The accessible name. */
    'aria-label': string
    /** Dropdown menu content. */
    children: React.ReactNode
    /** The icon to display. */
    icon: React.ReactNode
    /** Optional notification badge. */
    hasBadge?: boolean
  }
}

function TopBarNavIconMenuItem({
  'aria-label': ariaLabel,
  children,
  icon,
  hasBadge,
  className,
  ...rest
}: TopBarNavIconMenuItem.Props) {
  return (
    <li className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            {...rest}
            aria-label={ariaLabel}
            className={cn(
              'relative inline-flex cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-2 text-neutral-400 outline-none',
              'hover:bg-fill-neutral-lightest',
              'focus-visible:outline-2 focus-visible:outline-ring',
              className,
            )}
            type="button"
          >
            <span className="flex size-6 items-center justify-center text-inherit [&>svg]:size-full" aria-hidden="true">
              {icon}
            </span>
            {hasBadge && <span className="absolute right-[5px] top-[5px] size-2 rounded-full bg-destructive" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">{children}</DropdownMenuContent>
      </DropdownMenu>
    </li>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavIconItemButton — icon-only button (not dropdown, no <li>)
// Used for overflow menu trigger and similar standalone buttons.
// ---------------------------------------------------------------------------

export namespace TopBarNavIconItemButton {
  export interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /** The accessible name. */
    'aria-label': string
    /** The icon to display. */
    icon: React.ReactNode
    /** Optional notification badge. */
    hasBadge?: boolean
  }
}

function TopBarNavIconItemButton({
  'aria-label': ariaLabel,
  icon,
  hasBadge,
  className,
  ...rest
}: TopBarNavIconItemButton.Props) {
  return (
    <button
      {...rest}
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-2 text-neutral-400 outline-none',
        'hover:bg-fill-neutral-lightest',
        'focus-visible:outline-2 focus-visible:outline-ring',
        className,
      )}
      type="button"
    >
      <span className="flex size-6 items-center justify-center text-inherit [&>svg]:size-full" aria-hidden="true">
        {icon}
      </span>
      {hasBadge && <span className="absolute right-[5px] top-[5px] size-2 rounded-full bg-destructive" />}
    </button>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavSearch — responsive container showing button vs icon
// ---------------------------------------------------------------------------

export namespace TopBarNavSearch {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    /** The search button to display when there is enough space (>= 150px). */
    button?: React.ReactNode
    /** The icon item to display when space is limited (< 150px). */
    iconItem?: React.ReactNode
  }
}

function TopBarNavSearch({ button, iconItem, className, ...rest }: TopBarNavSearch.Props) {
  return (
    <div {...rest} className={cn('@container/nav-search', className)}>
      <div className="hidden @min-[150px]/nav-search:block">{button}</div>
      <div className="block @min-[150px]/nav-search:hidden">{iconItem}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavSearchButton — wide search button with placeholder + shortcut
// ---------------------------------------------------------------------------

export namespace TopBarNavSearchButton {
  export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Keyboard shortcut indicator text (e.g. "Ctrl+K" or "⌘K"). */
    shortcut?: React.ReactNode
    /** A click handler that launches the search experience. */
    onClick: React.MouseEventHandler<HTMLButtonElement>
  }
}

function TopBarNavSearchButton({ shortcut, onClick, className, ...rest }: TopBarNavSearchButton.Props) {
  return (
    <button
      {...rest}
      onClick={onClick}
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-2 overflow-hidden rounded-sm border-none bg-fill-neutral-lightest p-2 outline-none',
        'hover:bg-fill-neutral-light',
        'focus-visible:outline-2 focus-visible:outline-ring',
        className,
      )}
    >
      <span className="flex size-4 shrink-0 items-center text-icon-primary [&>svg]:size-full" aria-hidden="true">
        <SearchIcon />
      </span>
      <span className="flex-1 text-left text-xs font-normal text-icon-primary">Search</span>
      {shortcut && (
        <kbd className="border-none bg-transparent p-0 font-[inherit] text-[0.6875rem] font-medium leading-[0.875rem] text-icon-primary">
          {shortcut}
        </kbd>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// TopBar.NavSearchIconItem — icon-only search button for narrow widths
// ---------------------------------------------------------------------------

export namespace TopBarNavSearchIconItem {
  export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** A click handler that launches the search experience. */
    onClick: React.MouseEventHandler<HTMLButtonElement>
  }
}

function TopBarNavSearchIconItem({
  'aria-label': ariaLabel,
  onClick,
  className,
  ...rest
}: TopBarNavSearchIconItem.Props) {
  return (
    <TopBarNavIconItemButton {...rest} aria-label={ariaLabel ?? 'Search'} icon={<SearchIcon />} onClick={onClick} />
  )
}

// ---------------------------------------------------------------------------
// TopBar.AvatarButton — avatar trigger button
// ---------------------------------------------------------------------------

export namespace TopBarAvatarButton {
  export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** The accessible name. @default 'Profile menu' */
    'aria-label'?: string
    /** The avatar text, typically user initials. Shown as a fallback when no image is available. */
    children: React.ReactNode
    /** An optional profile image URL. Falls back to the initials if it fails to load. */
    imageUrl?: string
  }
}

function TopBarAvatarButton({
  'aria-label': ariaLabel = 'Profile menu',
  children,
  className,
  imageUrl,
  ...rest
}: TopBarAvatarButton.Props) {
  return (
    <button
      {...rest}
      aria-label={ariaLabel}
      className={cn(
        'cursor-pointer rounded-full border-none bg-transparent p-0 outline-none',
        'focus-visible:bg-white focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-1',
        className,
      )}
    >
      <Avatar size="small" shape="circle" colour="primary">
        {imageUrl ? <AvatarImage src={imageUrl} alt="" /> : null}
        <AvatarFallback>{children}</AvatarFallback>
      </Avatar>
    </button>
  )
}

// ---------------------------------------------------------------------------
// TopBar.AvatarMenu — avatar button + dropdown menu combo
// ---------------------------------------------------------------------------

export namespace TopBarAvatarMenu {
  export interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /** The dropdown menu items. */
    children: React.ReactNode
    /** The avatar text, typically user initials. Shown as a fallback when no image is available. */
    initials: React.ReactNode
    /** An optional profile image URL. Falls back to the initials if it fails to load. */
    imageUrl?: string
  }
}

function TopBarAvatarMenu({ children, initials, className, imageUrl, ...rest }: TopBarAvatarMenu.Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <TopBarAvatarButton {...rest} className={className} imageUrl={imageUrl}>
          {initials}
        </TopBarAvatarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Attach sub-components to TopBar namespace
// ---------------------------------------------------------------------------

const TopBar = Object.assign(TopBarRoot, {
  BrandLogo: TopBarBrandLogo,
  MainNav: TopBarMainNav,
  NavItem: TopBarNavItem,
  NavMenuItem: TopBarNavMenuItem,
  SecondaryNav: TopBarSecondaryNav,
  NavIconItem: TopBarNavIconItem,
  NavIconMenuItem: TopBarNavIconMenuItem,
  NavIconItemButton: TopBarNavIconItemButton,
  NavSearch: TopBarNavSearch,
  NavSearchButton: TopBarNavSearchButton,
  NavSearchIconItem: TopBarNavSearchIconItem,
  AvatarButton: TopBarAvatarButton,
  AvatarMenu: TopBarAvatarMenu,
})

export {
  TopBar,
  TopBarBrandLogo,
  TopBarMainNav,
  TopBarNavItem,
  TopBarNavMenuItem,
  TopBarSecondaryNav,
  TopBarNavIconItem,
  TopBarNavIconMenuItem,
  TopBarNavIconItemButton,
  TopBarNavSearch,
  TopBarNavSearchButton,
  TopBarNavSearchIconItem,
  TopBarAvatarButton,
  TopBarAvatarMenu,
  supportedAppNames,
}
export type { SupportedAppName }
